import React, { useState, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import apiFetch from "@wordpress/api-fetch";
import { addQueryArgs } from "@wordpress/url";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { __, _n, sprintf } from "@wordpress/i18n";

const columnHelper = createColumnHelper();

const columns = [
  columnHelper.accessor("acf.topic", {
    header: __("TOPIC", "battcon"),
    cell: (info) => info.getValue(),
    enableGlobalFilter: true,
  }),
  columnHelper.accessor("acf.author", {
    header: __("AUTHOR", "battcon"),
    enableGlobalFilter: true,
  }),
];

const ArchivedPaperList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [activeId, setActiveId] = useState(null);
  const dialogRef = useRef(null);
  const active = data.find((item) => item.id === activeId);

  useEffect(() => {
    if (activeId) dialogRef?.current.showModal();
    else {
      dialogRef.current?.close();
    }
  }, [activeId]);

  useEffect(() => {
    const queryParams = { post_per_page: -1 };
    apiFetch({
      path: addQueryArgs("/wp-json/wp/v2/archived-paper", queryParams),
    })
      .then((data) => {
        setData(data);
      })
      .catch((err) => {
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: "includesString",
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
  });

  return (
    <div className="container">
      <div className="archived-papers__wrapper">
        <header className="archived-papers__header">
          <h1>{__("Archived papers", "battcon")}</h1>
        </header>
        <label className="input">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15.8199 14.95L11.9725 11.1034C13.0876 9.76465 13.6437 8.0475 13.525 6.30919C13.4063 4.57088 12.622 2.94524 11.3353 1.77045C10.0486 0.595655 8.35844 -0.0378381 6.61653 0.00174965C4.87462 0.0413374 3.21503 0.750958 1.98299 1.98299C0.750958 3.21503 0.0413374 4.87462 0.00174965 6.61653C-0.0378381 8.35844 0.595655 10.0486 1.77045 11.3353C2.94524 12.622 4.57088 13.4063 6.30919 13.525C8.0475 13.6437 9.76465 13.0876 11.1034 11.9725L14.95 15.8199C15.0071 15.877 15.0749 15.9223 15.1496 15.9532C15.2242 15.9841 15.3042 16 15.3849 16C15.4657 16 15.5457 15.9841 15.6203 15.9532C15.6949 15.9223 15.7627 15.877 15.8199 15.8199C15.877 15.7627 15.9223 15.6949 15.9532 15.6203C15.9841 15.5457 16 15.4657 16 15.3849C16 15.3042 15.9841 15.2242 15.9532 15.1496C15.9223 15.0749 15.877 15.0071 15.8199 14.95ZM1.24642 6.77888C1.24642 5.68467 1.57089 4.61502 2.17881 3.70521C2.78672 2.7954 3.65078 2.08629 4.6617 1.66755C5.67263 1.24882 6.78502 1.13925 7.85821 1.35273C8.93141 1.5662 9.9172 2.09311 10.6909 2.86684C11.4647 3.64057 11.9916 4.62636 12.205 5.69955C12.4185 6.77275 12.309 7.88514 11.8902 8.89607C11.4715 9.90699 10.7624 10.771 9.85256 11.379C8.94275 11.9869 7.8731 12.3113 6.77888 12.3113C5.31208 12.3097 3.90582 11.7263 2.86864 10.6891C1.83145 9.65195 1.24805 8.24569 1.24642 6.77888Z"
              fill="var(--color-red)"
            />
          </svg>
          <input
            type="text"
            value={globalFilter}
            placeholder={__("Search for a paper...", "battcon")}
            onChange={(e) => table.setGlobalFilter(String(e.target.value))}
          />
        </label>
      </div>
      <div className="archived-papers__table">
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <th key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder ? null : (
                        <div
                          onClick={header.column.getToggleSortingHandler()}
                          title={
                            header.column.getCanSort()
                              ? header.column.getNextSortingOrder() === "asc"
                                ? __("Sort ascending", "battcon")
                                : header.column.getNextSortingOrder() === "desc"
                                ? __("Sort descending", "battcon")
                                : __("Clear sort", "battcon")
                              : undefined
                          }
                        >
                          <span>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </span>
                          <span>
                            A-Z
                            {{
                              asc: <i className="icon-chevron-up"></i>,
                              desc: <i className="icon-chevron-down"></i>,
                            }[header.column.getIsSorted()] ?? null}
                          </span>
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table
              .getRowModel()
              .rows.slice(0, 10)
              .map((row) => {
                return (
                  <tr key={row.id} onClick={() => setActiveId(row.original.id)}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </table>
        {loading && (
          <p className="archived-papers__loading extra-small">
            {__("Loading...", "battcon")}
          </p>
        )}
        {error && (
          <p className="archived-papers__error extra-small">
            {__("Error fetching archived papers:", "battcon")} {error.message}
          </p>
        )}
        {!loading && !error && (
          <div className="archived-papers__footer">
            <p className="extra-small">
              {sprintf(
                _n("Showing %d to %d of %d entries", "battcon"),
                (
                  table.getState().pagination.pageIndex *
                    table.getState().pagination.pageSize +
                  1
                ).toLocaleString(),
                Math.min(
                  (table.getState().pagination.pageIndex + 1) *
                    table.getState().pagination.pageSize,
                  table.getRowCount()
                ).toLocaleString(),
                table.getRowCount().toLocaleString()
              )}
            </p>
            <div className="archived-papers__navigation">
              <div className="pagination">
                <p className="extra-small">
                  {sprintf(
                    _n("Page %d of %d", "battcon"),
                    table.getState().pagination.pageIndex + 1,
                    table.getPageCount().toLocaleString()
                  )}
                </p>
                <p className="extra-small">|</p>
                <label>
                  {__("Go to page: ", "battcon")}
                  <input
                    value={table.getState().pagination.pageIndex + 1}
                    type="number"
                    min="1"
                    max={table.getPageCount()}
                    onChange={(e) => {
                      const page = e.target.value
                        ? Number(e.target.value) - 1
                        : 0;
                      table.setPageIndex(page);
                    }}
                  />
                </label>
              </div>
              <div className="buttons">
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  {__("PREV", "battcon")}
                </button>
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  {__("NEXT", "battcon")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {active && (
        <dialog ref={dialogRef} className="archived-papers__dialog wysiwyg">
          <button
            aria-label={__("Close modal", "battcon")}
            onClick={() => setActiveId(null)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="24"
              height="24"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M13 11.8l6.1-6.3-1-1-6.1 6.2-6.1-6.2-1 1 6.1 6.3-6.5 6.7 1 1 6.5-6.6 6.5 6.6 1-1z"></path>
            </svg>
          </button>
          {active.acf?.topic && <h3 className="h4">{active.acf.topic}</h3>}
          {active.acf?.author && (
            <p>
              <strong>{active.acf.author}</strong>
            </p>
          )}
          {active.acf?.summary && <p className="small">{active.acf.summary}</p>}
          {active.acf?.downloadable_file && (
            <a
              href={active.acf.downloadable_file}
              download
              className="button button--color-red"
            >
              {__("DOWNLOAD WHITEPAPER", "battcon")}
            </a>
          )}
        </dialog>
      )}
    </div>
  );
};

const rootElement = document.getElementById("archived-papers");

if (rootElement) {
  const root = createRoot(rootElement);
  root.render(<ArchivedPaperList />);
}
